import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import { AuthImage } from "@/components/AuthImage";
import { CommentThread } from "@/components/CommentThread";
import { Camera, Upload, X, Loader2, Share2, MessageCircle, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const CLASSES = ["Playgroup", "Nursery", "LKG", "UKG"];

export default function DailyUpdates() {
  const { user } = useAuth();
  const [updates, setUpdates] = useState([]);
  const [cls, setCls] = useState(user?.assigned_class || "");
  const [child, setChild] = useState("");
  const [children, setChildren] = useState([]);
  const [activity, setActivity] = useState("");
  const [meal, setMeal] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [shareOpen, setShareOpen] = useState(null); // { update, message, share_url, recipients }
  const [copied, setCopied] = useState(false);

  const load = async () => {
    const params = {};
    if (cls) params.class_name = cls;
    const [u, c] = await Promise.all([
      api.get("/daily-updates", { params }),
      user?.role !== "parent" ? api.get("/children") : Promise.resolve({ data: [] }),
    ]);
    setUpdates(u.data);
    setChildren(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cls]);

  const onFiles = async (files) => {
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data.path]);
      }
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const post = async () => {
    if (!activity.trim()) { toast.error("Add an activity note"); return; }
    try {
      await api.post("/daily-updates", {
        class_name: cls || null,
        child_id: child || null,
        date: new Date().toISOString().slice(0, 10),
        activity_note: activity,
        meal_note: meal,
        photos,
      });
      toast.success("Update posted!");
      setActivity(""); setMeal(""); setPhotos([]);
      load();
    } catch { toast.error("Could not post"); }
  };

  const canPost = user?.role === "admin" || user?.role === "teacher";

  const openShare = async (u) => {
    try {
      const { data } = await api.post(`/daily-updates/${u.id}/broadcast`);
      setShareOpen({ update: u, ...data });
      setCopied(false);
    } catch {
      toast.error("Could not prepare share");
    }
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(shareOpen.message);
      setCopied(true);
      toast.success("Message copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };

  const openAll = () => {
    (shareOpen.recipients || []).forEach((r, i) => {
      // stagger to avoid popup blocker
      setTimeout(() => window.open(r.wa_link, "_blank", "noopener"), i * 200);
    });
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-black">Daily Updates</h1>
        <p className="text-muted-foreground font-medium">Tiny moments, everyday memories.</p>
      </header>

      {canPost && (
        <Card className="rounded-3xl p-6 space-y-4">
          <div className="flex flex-wrap gap-3">
            {user?.role !== "teacher" ? (
              <Select value={cls || "__none"} onValueChange={(v) => setCls(v === "__none" ? "" : v)}>
                <SelectTrigger data-testid="du-class" className="h-11 rounded-full w-48"><SelectValue placeholder="Class" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— None —</SelectItem>
                  {CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <div className="chip-green px-4 py-2 rounded-full font-semibold text-sm">{cls}</div>
            )}
            <Select value={child || "__none"} onValueChange={(v) => setChild(v === "__none" ? "" : v)}>
              <SelectTrigger className="h-11 rounded-full w-64"><SelectValue placeholder="Specific child (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— Class-wide —</SelectItem>
                {children.filter((c) => !cls || c.class_name === cls).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Activity Note</Label>
            <Textarea data-testid="du-activity" value={activity} onChange={(e) => setActivity(e.target.value)} placeholder="What did the little ones do today?" className="rounded-2xl min-h-[80px]" />
          </div>
          <div>
            <Label>Meal Note</Label>
            <Input value={meal} onChange={(e) => setMeal(e.target.value)} placeholder="Snack & lunch" className="rounded-xl h-11" />
          </div>
          <div>
            <Label>Photos</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {photos.map((p) => (
                <div key={p} className="relative">
                  <AuthImage path={p} className="w-24 h-24 object-cover rounded-2xl" />
                  <button onClick={() => setPhotos(photos.filter((x) => x !== p))} className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-6 h-6 flex items-center justify-center">
                    <X size={14} />
                  </button>
                </div>
              ))}
              <label className="w-24 h-24 rounded-2xl border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:bg-muted transition-colors">
                {uploading ? <Loader2 className="animate-spin" size={20} /> : <>
                  <Upload size={18} className="text-muted-foreground" />
                  <span className="text-[10px] font-semibold text-muted-foreground mt-1">Add photo</span>
                </>}
                <input data-testid="du-photo" type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e.target.files)} />
              </label>
            </div>
          </div>
          <div className="flex justify-end">
            <Button data-testid="du-post" onClick={post} className="rounded-full h-12 px-6 font-bold">
              <Camera size={16} /> Post Update
            </Button>
          </div>
        </Card>
      )}

      <div className="space-y-4">
        {updates.length === 0 && (
          <div className="text-center py-16 text-muted-foreground font-medium">No updates yet.</div>
        )}
        {updates.map((u) => (
          <Card key={u.id} className="rounded-3xl p-6 animate-float-in">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-extrabold">{u.class_name ? `📚 ${u.class_name}` : "🎯 Individual"} · {u.date}</div>
                <div className="text-xs text-muted-foreground">Posted by {u.posted_by}</div>
              </div>
              {canPost && (
                <Button
                  data-testid={`share-${u.id}`}
                  size="sm"
                  variant="outline"
                  className="rounded-full border-primary/40 text-primary hover:bg-primary/10 font-bold"
                  onClick={() => openShare(u)}
                >
                  <Share2 size={14} /> Share on WhatsApp
                </Button>
              )}
            </div>
            <p className="mb-2">{u.activity_note}</p>
            {u.meal_note && <p className="text-sm text-muted-foreground">🍎 {u.meal_note}</p>}
            {u.photos?.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-4">
                {u.photos.map((p) => (
                  <AuthImage key={p} path={p} className="w-full aspect-square object-cover rounded-2xl" />
                ))}
              </div>
            )}
            <CommentThread updateId={u.id} />
          </Card>
        ))}
      </div>

      {/* WhatsApp broadcast dialog */}
      <Dialog open={!!shareOpen} onOpenChange={(v) => !v && setShareOpen(null)}>
        <DialogContent className="rounded-3xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="text-primary" size={20} /> Broadcast on WhatsApp
            </DialogTitle>
          </DialogHeader>
          {shareOpen && (
            <div className="space-y-4">
              <div className="rounded-2xl bg-muted/60 p-4 text-sm whitespace-pre-wrap font-medium">
                {shareOpen.message}
              </div>
              <div className="flex gap-2">
                <Button data-testid="share-copy" variant="outline" size="sm" onClick={copyMessage} className="rounded-full">
                  {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy message"}
                </Button>
                {shareOpen.recipients?.length > 1 && (
                  <Button data-testid="share-open-all" size="sm" onClick={openAll} className="rounded-full">
                    <Share2 size={14} /> Open all ({shareOpen.recipients.length})
                  </Button>
                )}
              </div>
              <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                {shareOpen.recipients?.length === 0 && (
                  <div className="text-sm text-muted-foreground py-4 text-center">
                    No parent phone numbers found for this update.
                  </div>
                )}
                {shareOpen.recipients?.map((r) => (
                  <a
                    key={r.phone}
                    href={r.wa_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`wa-${r.phone}`}
                    className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-border hover:border-primary/50 hover:-translate-y-0.5 transition-all"
                  >
                    <div className="w-10 h-10 rounded-xl chip-green flex items-center justify-center">
                      <MessageCircle size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate">{r.parent_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{r.child_name} · {r.phone}</div>
                    </div>
                    <span className="chip-green px-3 py-1 rounded-full text-xs font-bold">Send</span>
                  </a>
                ))}
              </div>
              <div className="text-xs text-muted-foreground">
                Tip: WhatsApp will open with the message pre-filled. Photos can be attached from the album link inside the message.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
